// src/stores/attendanceStore.ts
// =============================================
// מנהל מצב נוכחות - מנגנון סנכרון יעיל
// =============================================
// עקרונות:
// 1. תמונות נשמרות ב-localStorage (מפתח נפרד לכל ילד)
// 2. FullSync - פעם ביום, מקבל את כל הנתונים כולל תמונות
// 3. DeltaSync - מקבל רק שינויים (חדשים/נמחקים/מעודכנים)
// 4. סנכרון חכם: 15 דקות אם לא כולם הגיעו, 30 דקות אם כולם הגיעו
// =============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { attendanceApi, syncApi } from '../services/api';
import type { ChildSyncDto, KindergartenDaySettings, SyncResponse, ChildUpdateDto, ClosedDayInfo } from '@kipikid/shared';

// ===============================================
// Constants - Photo Storage
// ===============================================
const PHOTO_KEY_PREFIX = 'kipikid-photo-';
const PHOTO_METADATA_KEY = 'kipikid-photo-metadata';

// ===============================================
// Types
// ===============================================

export interface CachedChild {
  publicId: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;           // URL מהשרת (עם SAS)
  lastArrivalTime?: string;    // שעת הגעה אחרונה היסטורית (למיון)
  expectedArrivalTime?: string; // שעת הגעה צפויה להיום
  gender?: number;             // 1=זכר, 2=נקבה
  updatedAt?: string;          // תאריך עדכון אחרון
  photoUpdatedAt?: string;     // תאריך עדכון תמונה
}

export interface AttendanceChild extends CachedChild {
  status: 'expected' | 'arrived' | 'absent_notified' | 'picked_up';
  arrivalTime?: string;        // שעת הגעה היום
  arrivalSource?: 'tablet' | 'parent_app' | 'staff' | 'bletag';
}

interface PhotoMetadata {
  [childPublicId: string]: {
    updatedAt: string;
    size: number;
  };
}

interface AttendanceState {
  // Data
  children: AttendanceChild[];
  lastFullSync: string | null;
  lastDeltaSync: string | null;
  kindergartenSettings: KindergartenDaySettings | null;
  lastActivityTime: number;    // timestamp של פעילות אחרונה (סימון נוכחות)
  lastBlobVersion: number;     // Daily-prep blob version for change detection
  closedDayInfo: ClosedDayInfo | null; // מידע על יום סגור (חג/חופשה)

  // Status
  isLoading: boolean;
  isSyncing: boolean;
  syncError: string | null;
  hasUpdates: boolean;

  // Actions - Sync
  performMorningSync: (kindergartenPublicId: string, retryCount?: number) => Promise<void>;
  performDeltaSync: (kindergartenPublicId: string) => Promise<void>;
  checkForUpdates: (kindergartenPublicId: string) => Promise<boolean>;

  // Actions - Attendance
  markArrival: (childPublicId: string, kindergartenPublicId: string) => Promise<{ success: boolean; hasUpdates: boolean }>;
  cancelArrival: (childPublicId: string, kindergartenPublicId: string) => Promise<boolean>;

  // Actions - Local Updates
  updateChildStatus: (childPublicId: string, status: AttendanceChild['status'], arrivalTime?: string) => void;
  removeChild: (childPublicId: string) => void;
  updateLastActivity: () => void;

  // Actions - Utils
  clearCache: () => void;
  getChildPhoto: (childPublicId: string) => string | undefined;
  resetStore: () => void;
  needsFullSync: () => boolean;
  allChildrenArrived: () => boolean;
}

// ===============================================
// Time Helpers
// ===============================================

/**
 * Extract HH:mm from an ISO datetime string WITHOUT timezone conversion.
 * Backend stores Israel time but labels it as UTC (Z suffix).
 * Using new Date() would re-convert to local time, adding +2/+3 hours.
 * Instead, extract the time portion directly from the string.
 */
function extractTimeFromISO(isoString?: string): string | undefined {
  if (!isoString) return undefined;
  // Already in HH:mm format
  if (/^\d{2}:\d{2}$/.test(isoString)) return isoString;
  // HH:mm:ss format
  if (/^\d{2}:\d{2}:\d{2}$/.test(isoString)) return isoString.substring(0, 5);
  // ISO format: "2026-02-16T09:03:00Z" or "2026-02-16T09:03:00"
  const match = isoString.match(/T(\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  // Fallback: try to find HH:mm pattern
  const timeMatch = isoString.match(/(\d{2}):(\d{2})/);
  if (timeMatch) return `${timeMatch[1]}:${timeMatch[2]}`;
  return isoString;
}

// ===============================================
// Error Helpers
// ===============================================

function isAuthError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { status?: number } };
    return axiosError.response?.status === 401 || axiosError.response?.status === 403;
  }
  return false;
}

// ===============================================
// Photo Storage Helpers
// ===============================================

function savePhotoToStorage(childPublicId: string, base64: string): void {
  try {
    localStorage.setItem(`${PHOTO_KEY_PREFIX}${childPublicId}`, base64);

    // Update metadata
    const metadata = getPhotoMetadata();
    metadata[childPublicId] = {
      updatedAt: new Date().toISOString(),
      size: base64.length
    };
    localStorage.setItem(PHOTO_METADATA_KEY, JSON.stringify(metadata));
  } catch (e) {
    console.error('[PhotoStorage] Failed to save photo:', e);
    // אם localStorage מלא, ננסה לנקות תמונות ישנות
    cleanupOldPhotos();
  }
}

function getPhotoFromStorage(childPublicId: string): string | null {
  return localStorage.getItem(`${PHOTO_KEY_PREFIX}${childPublicId}`);
}

function removePhotoFromStorage(childPublicId: string): void {
  localStorage.removeItem(`${PHOTO_KEY_PREFIX}${childPublicId}`);

  const metadata = getPhotoMetadata();
  delete metadata[childPublicId];
  localStorage.setItem(PHOTO_METADATA_KEY, JSON.stringify(metadata));
}

function getPhotoMetadata(): PhotoMetadata {
  try {
    const stored = localStorage.getItem(PHOTO_METADATA_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function cleanupOldPhotos(): void {
  // מחיקת תמונות ישנות אם localStorage מלא
  console.warn('[PhotoStorage] Cleaning up old photos...');
  const metadata = getPhotoMetadata();
  const entries = Object.entries(metadata).sort((a, b) =>
    new Date(a[1].updatedAt).getTime() - new Date(b[1].updatedAt).getTime()
  );

  // מחיקת 20% מהתמונות הישנות
  const toRemove = Math.ceil(entries.length * 0.2);
  for (let i = 0; i < toRemove && i < entries.length; i++) {
    removePhotoFromStorage(entries[i][0]);
  }
}

function savePhotosFromSync(children: ChildSyncDto[]): void {
  for (const child of children) {
    if (child.photoBase64) {
      savePhotoToStorage(child.publicId, child.photoBase64);
    }
  }
}

// ===============================================
// Helper: Convert ChildSyncDto to AttendanceChild
// ===============================================

function syncDtoToAttendanceChild(dto: ChildSyncDto, currentChildren: AttendanceChild[]): AttendanceChild {
  // שמירת סטטוס קיים אם יש
  const existing = currentChildren.find(c => c.publicId === dto.publicId);

  return {
    publicId: dto.publicId,
    firstName: dto.firstName || existing?.firstName || '',
    lastName: dto.lastName || existing?.lastName || '',
    photoUrl: dto.photoUrl || existing?.photoUrl,
    expectedArrivalTime: (dto.expectedArrivalTime || existing?.expectedArrivalTime)?.substring(0, 5),
    gender: dto.gender ?? existing?.gender,
    updatedAt: dto.updatedAt,
    photoUpdatedAt: dto.photoUpdatedAt,
    status: existing?.status || 'expected',
    arrivalTime: existing?.arrivalTime,
    arrivalSource: existing?.arrivalSource,
  };
}

// ===============================================
// Store
// ===============================================

export const useAttendanceStore = create<AttendanceState>()(
  persist(
    (set, get) => ({
      // Initial State
      children: [],
      lastFullSync: null,
      lastDeltaSync: null,
      kindergartenSettings: null,
      lastActivityTime: Date.now(),
      lastBlobVersion: 0,
      closedDayInfo: null,
      isLoading: false,
      isSyncing: false,
      syncError: null,
      hasUpdates: false,

      // ========================================
      // Morning Sync (Full Sync)
      // ========================================
      performMorningSync: async (kindergartenPublicId: string, retryCount = 0) => {
        const MAX_RETRIES = 3;
        const RETRY_DELAYS = [3000, 5000, 10000]; // 3s, 5s, 10s

        set({ isSyncing: true, syncError: null });

        try {
          console.log(`[Sync] Starting FULL sync for: ${kindergartenPublicId}${retryCount > 0 ? ` (retry ${retryCount}/${MAX_RETRIES})` : ''}`);
          const response = await syncApi.fullSync(kindergartenPublicId);
          console.log('[Sync] Full sync response:', response);

          if (!response.success || !response.data) {
            throw new Error(response.message || 'Sync failed');
          }

          const { expectedChildren, arrivedChildren, parameters, daySettings, newChildren, closedDayInfo } = response.data;

          // שמירת תמונות ב-localStorage
          if (expectedChildren) {
            savePhotosFromSync(expectedChildren);
          }
          if (newChildren) {
            savePhotosFromSync(newChildren);
          }

          // המרת הילדים לפורמט המקומי
          const attendanceChildren: AttendanceChild[] = (expectedChildren || []).map(child => {
            const arrived = arrivedChildren?.find(a => a.childPublicId === child.publicId);

            return {
              publicId: child.publicId,
              firstName: child.firstName || '',
              lastName: child.lastName || '',
              photoUrl: child.photoUrl,
              expectedArrivalTime: child.expectedArrivalTime?.substring(0, 5),
              gender: child.gender,
              updatedAt: child.updatedAt,
              photoUpdatedAt: child.photoUpdatedAt,
              status: arrived ? 'arrived' : 'expected',
              arrivalTime: extractTimeFromISO(arrived?.arrivedAt),
              arrivalSource: arrived?.source?.toLowerCase() as AttendanceChild['arrivalSource'],
            };
          });

          // עדכון הגדרות הגן
          const settings = daySettings ? {
            openTime: daySettings.openTime,
            closeTime: daySettings.closeTime,
            displaySwitchTime: daySettings.displaySwitchTime,
            morningSyncTime: daySettings.morningSyncTime,
          } : null;

          // Use server's syncAt (simulated time during simulation) instead of local new Date()
          const serverSyncAt = response.data.syncAt || new Date().toISOString();
          set({
            children: attendanceChildren,
            lastFullSync: serverSyncAt,
            lastDeltaSync: serverSyncAt,
            kindergartenSettings: settings,
            lastBlobVersion: response.data.blobVersion || 0,
            closedDayInfo: closedDayInfo || null,
            isSyncing: false,
          });

          console.log(`[Sync] Full sync complete. ${attendanceChildren.length} children loaded. BlobVersion: ${response.data.blobVersion || 0}`);

        } catch (error) {
          console.error(`[Sync] Full sync failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);

          // שגיאת אימות - לא לנסות שוב (ה-interceptor כבר ניסה לחדש את הטוקן)
          if (isAuthError(error)) {
            console.error('[Sync] Authentication failed - token may be expired');
            set({
              syncError: 'אימות נכשל - נדרשת כניסה מחדש',
              isSyncing: false,
            });
            return; // לא לנסות retry על שגיאות אימות
          }

          // שגיאות רשת/שרת - ניסיון חוזר אם יש עוד ניסיונות ואין ילדים ב-state
          if (retryCount < MAX_RETRIES && get().children.length === 0) {
            const delay = RETRY_DELAYS[retryCount] || 10000;
            console.log(`[Sync] Retrying full sync in ${delay / 1000}s...`);
            set({ isSyncing: false, syncError: `סנכרון נכשל, מנסה שוב (${retryCount + 1}/${MAX_RETRIES})...` });

            await new Promise(resolve => setTimeout(resolve, delay));
            return get().performMorningSync(kindergartenPublicId, retryCount + 1);
          }

          set({
            syncError: error instanceof Error ? error.message : 'סנכרון נכשל',
            isSyncing: false,
          });
        }
      },

      // ========================================
      // Delta Sync
      // ========================================
      performDeltaSync: async (kindergartenPublicId: string) => {
        const { lastDeltaSync, lastFullSync, children } = get();
        const lastSync = lastDeltaSync || lastFullSync;

        // אם אין סנכרון קודם או אין ילדים - נעשה full sync
        if (!lastSync || children.length === 0) {
          console.log('[Sync] No previous sync or no children - performing full sync');
          return get().performMorningSync(kindergartenPublicId);
        }

        try {
          console.log('[Sync] Performing delta sync since:', lastSync);
          const response = await syncApi.deltaSync(kindergartenPublicId, lastSync);

          if (!response.success || !response.data) return;

          const {
            expectedChildren,
            arrivedChildren,
            newChildren,
            removedChildIds,
            updatedChildren,
            daySettings,
            closedDayInfo
          } = response.data;

          // אם היום סגור - עדכון ה-state וחזרה
          if (closedDayInfo) {
            set({ closedDayInfo, lastDeltaSync: response.data.syncAt || new Date().toISOString() });
            return;
          }

          set(state => {
            let updatedChildrenList = [...state.children];

            // 1. הוספת ילדים חדשים
            if (newChildren && newChildren.length > 0) {
              console.log('[Sync] Adding new children:', newChildren.length);
              savePhotosFromSync(newChildren);

              for (const newChild of newChildren) {
                if (!updatedChildrenList.find(c => c.publicId === newChild.publicId)) {
                  updatedChildrenList.push(syncDtoToAttendanceChild(newChild, []));
                }
              }
            }

            // 2. הסרת ילדים שנמחקו
            if (removedChildIds && removedChildIds.length > 0) {
              console.log('[Sync] Removing children:', removedChildIds.length);
              for (const removedId of removedChildIds) {
                removePhotoFromStorage(removedId);
                updatedChildrenList = updatedChildrenList.filter(c => c.publicId !== removedId);
              }
            }

            // 3. עדכון ילדים שהשתנו
            if (updatedChildren && updatedChildren.length > 0) {
              console.log('[Sync] Updating children:', updatedChildren.length);

              for (const update of updatedChildren) {
                const index = updatedChildrenList.findIndex(c => c.publicId === update.publicId);
                if (index >= 0) {
                  // עדכון רק השדות שהשתנו
                  if (update.firstName) updatedChildrenList[index].firstName = update.firstName;
                  if (update.lastName) updatedChildrenList[index].lastName = update.lastName;
                  if (update.expectedArrivalTime) updatedChildrenList[index].expectedArrivalTime = update.expectedArrivalTime.substring(0, 5);
                  if (update.photoBase64) {
                    savePhotoToStorage(update.publicId, update.photoBase64);
                  }
                }
              }
            }

            // 4. עדכון נוכחויות חדשות
            if (arrivedChildren && arrivedChildren.length > 0) {
              for (const arrival of arrivedChildren) {
                const index = updatedChildrenList.findIndex(c => c.publicId === arrival.childPublicId);
                if (index >= 0) {
                  updatedChildrenList[index] = {
                    ...updatedChildrenList[index],
                    status: 'arrived',
                    arrivalTime: extractTimeFromISO(arrival.arrivedAt),
                    arrivalSource: arrival.source?.toLowerCase() as AttendanceChild['arrivalSource'],
                  };
                }
              }
            }

            // 5. סנכרון רשימת הילדים הצפויים (עדכון סטטוס לא מגיע)
            if (expectedChildren && expectedChildren.length > 0) {
              const expectedIds = new Set(expectedChildren.map(c => c.publicId));

              // ילדים שהיו expected אבל כבר לא ברשימה - סימון כ-absent_notified
              for (let i = 0; i < updatedChildrenList.length; i++) {
                const child = updatedChildrenList[i];
                if (child.status === 'expected' && !expectedIds.has(child.publicId)) {
                  console.log('[Sync] Marking child as absent_notified:', child.firstName);
                  updatedChildrenList[i] = { ...child, status: 'absent_notified' };
                }
              }
            }

            // עדכון הגדרות
            const settings = daySettings ? {
              openTime: daySettings.openTime,
              closeTime: daySettings.closeTime,
              displaySwitchTime: daySettings.displaySwitchTime,
              morningSyncTime: daySettings.morningSyncTime,
            } : state.kindergartenSettings;

            // Use server's syncAt (simulated time during simulation) instead of local new Date()
            return {
              children: updatedChildrenList,
              lastDeltaSync: response.data.syncAt || new Date().toISOString(),
              kindergartenSettings: settings,
              lastBlobVersion: response.data.blobVersion || state.lastBlobVersion,
              closedDayInfo: null,
              hasUpdates: false,
            };
          });

          console.log(`[Sync] Delta sync complete. BlobVersion: ${response.data.blobVersion || 0}`);

        } catch (error) {
          console.error('[Sync] Delta sync failed:', error);
          if (isAuthError(error)) {
            set({ syncError: 'אימות נכשל - נדרשת כניסה מחדש' });
          }
        }
      },

      // ========================================
      // Check for Updates
      // ========================================
      checkForUpdates: async (kindergartenPublicId: string) => {
        const { lastDeltaSync, lastFullSync } = get();
        const lastSync = lastDeltaSync || lastFullSync;

        if (!lastSync) return true;

        try {
          const result = await syncApi.checkForUpdates(kindergartenPublicId, lastSync);
          if (result.hasUpdates) {
            set({ hasUpdates: true });
          }
          return result.hasUpdates;
        } catch {
          return false;
        }
      },

      // ========================================
      // Mark Arrival
      // ========================================
      markArrival: async (childPublicId: string, kindergartenPublicId: string) => {
        const timeStr = new Date().toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        // עדכון מקומי מיידי (אופטימיסטי)
        get().updateChildStatus(childPublicId, 'arrived', timeStr);
        get().updateLastActivity();

        try {
          const response = await attendanceApi.markArrival({
            childPublicId,
            kindergartenPublicId,
            source: 'Tablet',
          });

          if (!response.success) {
            get().updateChildStatus(childPublicId, 'expected', undefined);
            return { success: false, hasUpdates: false };
          }

          // Use server time (supports simulation) to correct the optimistic update
          const markedAt = (response.data as any)?.markedAt || (response.data as any)?.MarkedAt;
          if (markedAt) {
            const match = String(markedAt).match(/T(\d{2}:\d{2})/);
            if (match) {
              get().updateChildStatus(childPublicId, 'arrived', match[1]);
            }
          }

          const hasUpdates = (response as any).hasUpdates || false;
          if (hasUpdates) {
            set({ hasUpdates: true });
          }

          return { success: true, hasUpdates };

        } catch (error) {
          console.error('[Attendance] Mark arrival failed:', error);
          get().updateChildStatus(childPublicId, 'expected', undefined);
          return { success: false, hasUpdates: false };
        }
      },

      // ========================================
      // Cancel Arrival
      // ========================================
      cancelArrival: async (childPublicId: string, kindergartenPublicId: string) => {
        const child = get().children.find(c => c.publicId === childPublicId);
        if (!child) return false;

        get().updateChildStatus(childPublicId, 'expected', undefined);

        try {
          const response = await attendanceApi.cancelArrival(
            childPublicId,
            kindergartenPublicId,
            'Cancelled from tablet'
          );

          if (!response.success) {
            get().updateChildStatus(childPublicId, 'arrived', child.arrivalTime);
            return false;
          }

          return true;

        } catch (error) {
          console.error('[Attendance] Cancel arrival failed:', error);
          get().updateChildStatus(childPublicId, 'arrived', child.arrivalTime);
          return false;
        }
      },

      // ========================================
      // Local Updates
      // ========================================
      updateChildStatus: (childPublicId, status, arrivalTime) => {
        set(state => ({
          children: state.children.map(child =>
            child.publicId === childPublicId
              ? { ...child, status, arrivalTime, arrivalSource: status === 'arrived' ? 'tablet' : undefined }
              : child
          ),
        }));
      },

      removeChild: (childPublicId) => {
        removePhotoFromStorage(childPublicId);
        set(state => ({
          children: state.children.filter(c => c.publicId !== childPublicId),
        }));
      },

      updateLastActivity: () => {
        set({ lastActivityTime: Date.now() });
      },

      // ========================================
      // Utils
      // ========================================
      clearCache: () => {
        // ניקוי תמונות מ-localStorage
        const metadata = getPhotoMetadata();
        for (const childId of Object.keys(metadata)) {
          removePhotoFromStorage(childId);
        }
        localStorage.removeItem(PHOTO_METADATA_KEY);

        set({
          children: [],
          lastFullSync: null,
          lastDeltaSync: null,
          closedDayInfo: null,
        });
      },

      getChildPhoto: (childPublicId) => {
        // קודם מנסים מ-localStorage
        const cached = getPhotoFromStorage(childPublicId);
        if (cached) return cached;

        // אחרת מה-state (URL)
        const child = get().children.find(c => c.publicId === childPublicId);
        return child?.photoUrl;
      },

      resetStore: () => {
        console.log('[Store] Resetting attendance store...');
        localStorage.removeItem('kipikid-attendance');
        set({
          children: [],
          lastFullSync: null,
          lastDeltaSync: null,
          kindergartenSettings: null,
          lastActivityTime: Date.now(),
          closedDayInfo: null,
          isLoading: false,
          isSyncing: false,
          syncError: null,
          hasUpdates: false,
        });
      },

      needsFullSync: () => {
        const { lastFullSync } = get();
        if (!lastFullSync) return true;

        // אם הסנכרון האחרון היה אתמול (או קודם)
        const lastSyncDate = new Date(lastFullSync).toDateString();
        const today = new Date().toDateString();
        return lastSyncDate !== today;
      },

      allChildrenArrived: () => {
        const { children } = get();
        const expected = children.filter(c => c.status === 'expected');
        return expected.length === 0 && children.length > 0;
      },
    }),
    {
      name: 'kipikid-attendance',
      partialize: (state) => ({
        // לא שומרים children ב-localStorage - תמיד נטען מהשרת
        lastFullSync: state.lastFullSync,
        lastDeltaSync: state.lastDeltaSync,
        kindergartenSettings: state.kindergartenSettings,
        lastActivityTime: state.lastActivityTime,
      }),
      merge: (persistedState: any, currentState: AttendanceState) => {
        return {
          ...currentState,
          lastFullSync: persistedState?.lastFullSync ?? currentState.lastFullSync,
          lastDeltaSync: persistedState?.lastDeltaSync ?? currentState.lastDeltaSync,
          kindergartenSettings: persistedState?.kindergartenSettings ?? currentState.kindergartenSettings,
          lastActivityTime: persistedState?.lastActivityTime ?? currentState.lastActivityTime,
        };
      },
    }
  )
);

// ===============================================
// Selectors
// ===============================================

export const selectExpectedChildren = (state: AttendanceState) =>
  state.children.filter(c => c.status === 'expected');

export const selectArrivedChildren = (state: AttendanceState) =>
  state.children.filter(c => c.status === 'arrived');

export const selectAbsentNotifiedChildren = (state: AttendanceState) =>
  state.children.filter(c => c.status === 'absent_notified');

